"use client";

import { ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  filterBookCallTimeZoneOptions,
  formatBookCallTimeZoneOption,
  groupBookCallTimeZoneOptions,
  listBookCallTimeZoneOptions,
} from "@/lib/booking/book-call-datetime";

type CcBookCallTimeZoneSelectProps = {
  locale: string;
  value: string;
  onChange: (timeZone: string) => void;
  ariaLabel: string;
  searchPlaceholder: string;
  emptyLabel: string;
};

export function CcBookCallTimeZoneSelect({
  locale,
  value,
  onChange,
  ariaLabel,
  searchPlaceholder,
  emptyLabel,
}: CcBookCallTimeZoneSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const allOptions = useMemo(
    () => listBookCallTimeZoneOptions(locale, value),
    [locale, value],
  );
  const filteredOptions = useMemo(
    () => filterBookCallTimeZoneOptions(allOptions, query),
    [allOptions, query],
  );
  const groupedOptions = useMemo(
    () => groupBookCallTimeZoneOptions(filteredOptions),
    [filteredOptions],
  );
  const selectedLabel = formatBookCallTimeZoneOption(value, locale);
  const showGroups = !query.trim();

  const selectOption = (timeZone: string) => {
    onChange(timeZone);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="cc-book-call-tz-select">
    <Popover
      modal={false}
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          className="cc-book-call-tz-select__trigger"
        >
          <span className="cc-book-call-tz-select__value">{selectedLabel}</span>
          <ChevronDown
            className="cc-book-call-tz-select__chevron"
            aria-hidden="true"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="cc-book-call-tz-select__popover z-[10001]"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          const content = event.currentTarget;
          if (!(content instanceof HTMLElement)) return;
          requestAnimationFrame(() => {
            content
              .querySelector<HTMLInputElement>('[data-slot="command-input"]')
              ?.focus();
          });
        }}
      >
        <Command shouldFilter={false} className="cc-book-call-tz-select__command">
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={setQuery}
            aria-label={searchPlaceholder}
          />
          <CommandList data-lenis-prevent>
            <CommandEmpty>{emptyLabel}</CommandEmpty>
            {showGroups
              ? groupedOptions.map((group) => (
                  <CommandGroup
                    key={group.region}
                    heading={group.regionLabel}
                  >
                    {group.options.map((option) => (
                      <CommandItem
                        key={option.id}
                        value={`${option.id} ${option.label}`}
                        data-checked={option.id === value ? true : undefined}
                        onSelect={() => selectOption(option.id)}
                      >
                        {option.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))
              : (
                  <CommandGroup>
                    {filteredOptions.map((option) => (
                      <CommandItem
                        key={option.id}
                        value={`${option.id} ${option.label}`}
                        data-checked={option.id === value ? true : undefined}
                        onSelect={() => selectOption(option.id)}
                      >
                        {option.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
    </div>
  );
}
